import { useNavigate } from "@tanstack/react-router";
import { useAutoRedirectCuandoAsignados } from "@/features/proveedor/hooks/use-auto-redirect-asignados";
import { useProveedoresAgrupados } from "@/features/proveedor/hooks/use-proveedores-agrupados";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { PageHeader, ReadOnlyBanner, Stack } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { estaPostEnvio } from "@/lib/estado-solicitud";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/proveedor";
import { ProveedorActions } from "./ProveedorActions";
import { SeccionRFC } from "./SeccionRFC";

/**
 * Container · orquesta los hooks de datos, calcula los flags derivados
 * (todosAsignados, faltantes, esMultiple) y mapea cada grupo a `<SeccionRFC>`.
 */
export function ProveedorPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);
  const { data: grupos } = useProveedoresAgrupados(id);

  const esDueno = useEsDueno(sol);
  // Post-envío, o viewer que no es el dueño (P29) · sin mutaciones.
  const soloLectura = estaPostEnvio(sol.estado) || !esDueno;
  const todosAsignados = grupos.every((g) => g.proveedorSeleccionado !== null);
  const faltantes = grupos.filter((g) => !g.proveedorSeleccionado).length;
  const esMultiple = sol.cfdis.length > 1;
  const destinoProrrateo = esMultiple ? ("/solicitudes/$id/prorrateo-multiple" as const) : ("/solicitudes/$id/prorrateo" as const);

  // El auto-avance a prorrateo es parte del flujo de CAPTURA · no aplica en
  // solo lectura (el viewer navega libremente entre pasos sin ser arrastrado).
  useAutoRedirectCuandoAsignados(todosAsignados, !soloLectura, () => navigate({ to: destinoProrrateo, params: { id }, replace: true }));

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title={`Proveedores · Solicitud #${sol.numeroSolicitud}`}
        description={grupos.length === 1 ? "1 RFC emisor único · selecciona el proveedor." : `${grupos.length} RFCs emisores únicos · selecciona un proveedor para cada uno.`}
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: `#${sol.numeroSolicitud}`, href: `/solicitudes/${id}` },
          { label: "Proveedor" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} paso="proveedor" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {soloLectura && <ReadOnlyBanner motivo={esDueno ? "post-envio" : "no-dueno"} mensaje="La asignación de proveedor no puede modificarse." />}
          {grupos.map((g) => (
            <SeccionRFC key={g.rfcEmisor} grupo={g} solicitudId={id} soloLectura={soloLectura} numeroSolicitud={sol.numeroSolicitud} />
          ))}
          <ProveedorActions
            todosAsignados={todosAsignados}
            faltantes={faltantes}
            soloLectura={soloLectura}
            onVolver={() => navigate({ to: "/solicitudes/$id/datos-generales", params: { id }, replace: true })}
            onContinuar={() => navigate({ to: destinoProrrateo, params: { id } })}
          />
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
